import React, {CSSProperties, ReactNode, useCallback, useState} from 'react';
import AntdTypography from 'antd/lib/typography';
import { extractDomProps } from '../../../utils/DomPropsExtractor';
import classnames from 'classnames';
import {Tooltip, TooltipProps} from '../tooltip';

export interface EllipsisConfig {
   /**
    * Max rows of content
    */
   rows?: number;

   /**
    * Whether to be expandable
    */
   expandable?: boolean;

   /**
    * Suffix of ellipsis content
    */
   suffix?: string;

   /**
    * Custom description of ellipsis
    */
   symbol?: React.ReactNode;

   /**
    * Show tooltip when ellipsis
    */
   tooltip?: boolean;

   /**
    * Called when expand content
    */
   onExpand?: React.MouseEventHandler<HTMLElement>;

   /**
    * Called when enter or leave ellipsis state
    */
   onEllipsis?: (ellipsis: boolean) => void;
}

export interface TypographyProps {
   prefixCls?: string;
   className?: string;
   style?: React.CSSProperties;
   children?: React.ReactNode;
}

/**
 * {@docCategory Text}
 */
export interface TextProps {
   /**
    * The id of the element
    */
   id?: string;

   /**
    * Content type
    */
   type?: 'secondary' | 'success' | 'warning' | 'danger';

   /**
    * Strikethrough style
    */
   strikethrough?: boolean;

   /**
    * Underline style
    */
   underline?: boolean;

   /**
    * Code style (e.g. for doing code blocks)
    */
   code?: boolean;

   /**
    * Keyboard style (e.g. for documenting key strokes)
    */
   keyboard?: boolean;

   /**
    * Mark style (e.g. highlighter)
    */
   mark?: boolean;

   /**
    * Bold style
    */
   strong?: boolean;

   /**
    * Disabled content
    */
   disabled?: boolean;

   /**
    * Display ellipsis when text overflows
    */
   ellipsis?: boolean | EllipsisConfig;

   /**
    * Sets a className
    */
   className?: string;

   /**
    * Sets styles
    */
   style?: CSSProperties;

   /**
    * The children
    */
   children?: ReactNode;

   /**
    * The theme
    */
   theme?: 'light' | 'dark';
}

export const Text = (props: TextProps) => {
   const { ellipsis } = props;

   const [isEllipsis, setEllipsis] = useState(false);

   const tooltip = (ellipsis && (ellipsis as any).tooltip);

   const classNames = classnames('uf-text', {'uf-text-dark': props.type === undefined && props.theme === 'dark'}, {'uf-text-secondary-dark': props.type === 'secondary'  && props.theme === 'dark'}, props.className);

   return ((tooltip) ?
        <Tooltip title={props.children}>
           <AntdTypography.Text
              {...extractDomProps(props)}
              id={props.id}
              type={props.type}
              underline={props.underline}
              delete={props.strikethrough}
              code={props.code}
              keyboard={props.keyboard}
              mark={props.mark}
              strong={props.strong}
              disabled={props.disabled}
              ellipsis={true}
              className={classNames}
              style={props.style}
           >
              {props.children}
           </AntdTypography.Text>
        </Tooltip>
      :
      <AntdTypography.Text
         {...extractDomProps(props)}
         id={props.id}
         type={props.type}
         underline={props.underline}
         delete={props.strikethrough}
         code={props.code}
         keyboard={props.keyboard}
         mark={props.mark}
         strong={props.strong}
         disabled={props.disabled}
         ellipsis={props.ellipsis}
         className={classNames}
         style={props.style}
      >
         {props.children}
      </AntdTypography.Text>
   );
};
